import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from '@headlessui/react'
import { AnchorProps } from '@headlessui/react/dist/internal/floating'
import clsx from 'clsx'
import { Fragment, ReactNode } from 'react'
import { PRIMARY_COLOR } from '../../constants'
import { LoadingOval } from '../spinner/Spinner'

const Dropdown = ({
  menuItems,
  menuButton,
  customMenuItems = undefined,
  onClickMenuItem,
  position = 'bottom end',
  className = '',
  closeOnClick = true,
  menuClassName = '',
  loading = false,
  disabled = false,
  menuItemClassName = '',
}: {
  menuButton: ReactNode | string
  onClickMenuItem?: (item: {
    name: string | ReactNode
    value: string | object | boolean | number
  }) => void
  menuItems?: (
    | {
        name: string | ReactNode
        value: string | object | boolean | number
        onClick?: () => void
        hidden?: boolean
        className?: string
      }
    | undefined
  )[]
  customMenuItems?: ReactNode
  position?: AnchorProps
  className?: string
  closeOnClick?: boolean
  menuClassName?: string
  menuItemClassName?: string
  loading?: boolean
  disabled?: boolean
}) => {
  return (
    <Menu
      as="div"
      className={`relative inline-block text-left max-h-fit ${className}`}
    >
      <MenuButton
        as={'a'}
        className={
          'cursor-pointer hover:scale-[0.98] transition-all duration-300 max-h-fit'
        }
        onClick={(e) => {
          e.stopPropagation()
        }}
        disabled={disabled}
      >
        {menuButton}
      </MenuButton>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <MenuItems
          className={clsx(
            `absolute 
              min-w-56 w-[--button-width]
              z-[999] mt-5
              overflow-y-scroll
              overflow-x-hidden bg-white
              rounded-2xl shadow-dropdown-custom
              focus:outline-none
              hide-scroll-bar
              ${menuClassName}
              `,
          )}
          anchor={position}
          portal
        >
          {loading && (
            <div className="py-8 flex items-center justify-center">
              <LoadingOval
                color={PRIMARY_COLOR}
                loaderHeight="25"
                loaderWidth="25"
              />
            </div>
          )}

          {!loading && customMenuItems !== undefined
            ? customMenuItems
            : undefined}

          {!loading && !customMenuItems
            ? menuItems
                ?.filter((item) => !item?.hidden && item !== undefined)
                ?.map((item, index) => {
                  const key = `menu-item-${index}`
                  return (
                    <MenuItem key={key}>
                      {({ focus }) => (
                        <button
                          type="button"
                          className={clsx(
                            `w-full text-sec-black text-sm ${menuItemClassName}`,
                            focus && 'bg-primary/5 text-black',
                            'block px-4 py-2 first:pt-4 last:pb-4 text-sm text-left',
                            item?.className ?? '',
                          )}
                          onClick={(e) => {
                            !closeOnClick && e.preventDefault()

                            if (onClickMenuItem) {
                              onClickMenuItem(item!)
                            }

                            item?.onClick?.()
                          }}
                        >
                          {item!.name}
                        </button>
                      )}
                    </MenuItem>
                  )
                })
            : undefined}

          {!loading &&
          !customMenuItems &&
          [0, undefined].includes(menuItems?.length) ? (
            <div className="text-sec-black text-sm py-8 text-center">
              No data found
            </div>
          ) : undefined}
        </MenuItems>
      </Transition>
    </Menu>
  )
}

export default Dropdown
